import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CampaignIcon from "@mui/icons-material/Campaign";
import { getRequest } from "../../api/apiServices";
import { getCampaignPrefill } from "../../api/apiList";
import CompaignForm from "../../components/Compaign/CompaigForm";
import {
  CampaignPrefillProvider,
} from "../../context/CampaignPrefillContext";
import { CampaignPrefillResponse } from "../../types/campaignPrefill";
import { seedCampaignPrefillForJob } from "../../utils/campaignPrefillStorage";
import Loader from "../../utils/Loading";

const CampaignCreatePage: React.FC = () => {
  const { jobSpecId } = useParams<{ jobSpecId: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<CampaignPrefillResponse | null>(null);
  const parsedJobSpecId = Number(jobSpecId);

  useEffect(() => {
    let isMounted = true;

    const loadPrefill = async () => {
      if (!jobSpecId || Number.isNaN(parsedJobSpecId)) {
        setError("Invalid job specification.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await getRequest(getCampaignPrefill(parsedJobSpecId));
        const data = response?.data?.data as CampaignPrefillResponse | undefined;

        if (!isMounted) return;

        if (!data) {
          setError("Unable to load campaign prefill data.");
          setIsLoading(false);
          return;
        }

        setPrefill(data);
        seedCampaignPrefillForJob(parsedJobSpecId, data);
        setIsLoading(false);
      } catch {
        if (!isMounted) return;
        setError("Failed to load campaign prefill. Please try again.");
        setIsLoading(false);
      }
    };

    loadPrefill();

    return () => {
      isMounted = false;
    };
  }, [jobSpecId, parsedJobSpecId]);

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 640, mx: "auto" }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Cannot start campaign
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => navigate("/jobs")}>
          Back to jobs
        </Button>
      </Box>
    );
  }

  if (!prefill?.eligibility?.canCreateCampaign) {
    return (
      <Box sx={{ p: 4, maxWidth: 720, mx: "auto" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
          <CampaignIcon sx={{ color: "#84E1CC" }} />
          <Typography variant="h5" fontWeight={700}>
            Campaign not available yet
          </Typography>
        </Box>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          This job specification is not ready for campaign creation. Complete the
          steps below first:
        </Typography>
        <List dense sx={{ mb: 3, bgcolor: "#f8fafc", borderRadius: 2 }}>
          {(prefill?.eligibility?.reasons?.length
            ? prefill.eligibility.reasons
            : ["Job summary must be approved and vacancy text must exist."]
          ).map((reason) => (
            <ListItem key={reason}>
              <ListItemText primary={reason} />
            </ListItem>
          ))}
        </List>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/jobs")}
          >
            Back to jobs
          </Button>
          {parsedJobSpecId > 0 && (
            <Button
              variant="contained"
              onClick={() => navigate(`/jobs/update-job/${parsedJobSpecId}`)}
            >
              Open job specification
            </Button>
          )}
        </Box>
      </Box>
    );
  }

  return (
    <CampaignPrefillProvider
      value={{
        jobSpecificationId: parsedJobSpecId,
        prefill,
        eligibility: prefill.eligibility,
        isLoading: false,
        error: null,
      }}
    >
      <CompaignForm />
    </CampaignPrefillProvider>
  );
};

export default CampaignCreatePage;
